import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  AlertTriangle,
  AlertCircle,
  Info,
  Play,
  CheckCircle,
  FileText,
  Loader,
  Wrench,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  ArrowUp,
  ArrowDown,
  TrendingUp,
  Zap,
  Settings,
  Clock,
  BarChart3,
  Image,
  Link2,
  X,
  Search,
} from "lucide-react";
import api from "../api/client";
import ScoreRing from "./ScoreRing";
import CrawlDashboard from "./CrawlDashboard";

const severityConfig = {
  critical: {
    icon: AlertTriangle,
    color: "text-red-400",
    bg: "bg-red-900/20",
    border: "border-red-800/30",
    label: "Errors",
    dotColor: "bg-red-500",
  },
  warning: {
    icon: AlertCircle,
    color: "text-amber-400",
    bg: "bg-amber-900/20",
    border: "border-amber-800/30",
    label: "Warnings",
    dotColor: "bg-amber-500",
  },
  info: {
    icon: Info,
    color: "text-blue-400",
    bg: "bg-blue-900/20",
    border: "border-blue-800/30",
    label: "Notices",
    dotColor: "bg-blue-500",
  },
};

const ISSUE_LABELS = {
  multiple_title_tags: "Multiple Title Tags",
  multiple_meta_descriptions: "Multiple Meta Descriptions",
  page_404: "404 Pages",
  http_error: "HTTP Errors",
  broken_image: "Broken Images",
  double_slash_url: "Double Slash in URL",
  redirect_chain: "Redirect Chains",
  link_to_broken: "Links to Broken Pages",
  mixed_content: "Mixed Content",
  "3xx_redirect": "3XX Redirects",
  missing_title: "Missing Title",
  title_too_long: "Title Too Long",
  title_too_short: "Title Too Short",
  missing_description: "Missing Meta Description",
  description_too_long: "Meta Description Too Long",
  description_too_short: "Meta Description Too Short",
  duplicate_title: "Duplicate Titles",
  duplicate_description: "Duplicate Meta Descriptions",
  missing_h1: "Missing H1",
  empty_h1: "Empty H1",
  multiple_h1: "Multiple H1 Tags",
  missing_alt: "Missing Alt Text",
  og_tags_incomplete: "Open Graph Incomplete",
  image_too_large: "Images Too Large",
  link_to_redirect: "Links to Redirects",
  orphan_page: "Orphan Pages",
  non_canonical_in_sitemap: "Non-Canonical in Sitemap",
  noindex_in_sitemap: "Noindex in Sitemap",
  hreflang_broken: "Broken Hreflang",
  missing_canonical: "Missing Canonical",
  canonical_mismatch: "Canonical Mismatch",
  redirected_javascript: "Redirected JavaScript",
  image_no_dimensions: "Images Missing Dimensions",
  image_not_lazy: "Images Not Lazy Loaded",
  thin_content: "Thin Content",
  title_changed: "Title Changed",
  word_count_changed: "Word Count Changed",
  broken_link: "Broken Links",
  slow_page: "Slow Pages",
};

export default function AuditIssues({ selectedSite }) {
  const [latestAudit, setLatestAudit] = useState(null);
  const [issues, setIssues] = useState([]);
  const [issueSummary, setIssueSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [expandedType, setExpandedType] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);
  const [historyTrend, setHistoryTrend] = useState([]);
  const [settings, setSettings] = useState(null);
  const [activeView, setActiveView] = useState("issues"); // issues | history | images | links
  const pollRef = useRef(null);

  const loadAuditData = useCallback(async () => {
    if (!selectedSite) return;
    try {
      const res = await api.get(`/websites/${selectedSite.id}/audits/`);
      const list = res.data.results || res.data;

      const latestCompleted = list.find((a) => a.status === "completed");
      const latest = list[0];

      if (latest && latest.status === "running") {
        setRunning(true);
        startPolling();
      }

      if (latestCompleted) {
        const detailRes = await api.get(`/audits/${latestCompleted.id}/`);
        setLatestAudit(detailRes.data);
        setIssues(detailRes.data.issues || []);
        setIssueSummary(detailRes.data.issue_summary || []);
      } else if (latest) {
        setLatestAudit(latest);
      }

      // Load history
      try {
        const histRes = await api.get(
          `/websites/${selectedSite.id}/audits/history/`
        );
        setAuditHistory(histRes.data.audits || []);
        setHistoryTrend(histRes.data.trend || []);
      } catch {}

      // Load settings
      try {
        const setRes = await api.get(
          `/websites/${selectedSite.id}/audits/settings/`
        );
        setSettings(setRes.data);
      } catch {}
    } catch {}
  }, [selectedSite]);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      if (!selectedSite) return;
      try {
        const res = await api.get(
          `/websites/${selectedSite.id}/audits/status/`
        );
        if (res.data.status === "completed" || res.data.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRunning(false);
          loadAuditData();
        } else {
          setLatestAudit((prev) => ({
            ...prev,
            pages_crawled: res.data.pages_crawled,
            pages_total: res.data.pages_total,
            current_phase: res.data.current_phase,
          }));
        }
      } catch {}
    }, 3000);
  }, [selectedSite, loadAuditData]);

  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!selectedSite) return;
    setLoading(true);
    setLatestAudit(null);
    setIssues([]);
    setIssueSummary([]);
    setExpandedType(null);
    loadAuditData().finally(() => setLoading(false));
  }, [selectedSite, loadAuditData]);

  const runAudit = (type = "full") => {
    if (!selectedSite || running) return;
    setRunning(true);
    api
      .post(`/websites/${selectedSite.id}/audits/run/`, { audit_type: type })
      .then(() => startPolling())
      .catch(() => setRunning(false));
  };

  const generateAuditPdf = () => {
    if (!selectedSite || !latestAudit) return;
    setGeneratingPdf(true);
    api
      .post(`/websites/${selectedSite.id}/reports/generate/`, {
        report_type: "audit",
        audit_id: latestAudit.id,
      })
      .then(() => {})
      .catch(() => {})
      .finally(() => setGeneratingPdf(false));
  };

  const saveSettings = async (newSettings) => {
    try {
      const res = await api.put(
        `/websites/${selectedSite.id}/audits/settings/`,
        newSettings
      );
      setSettings(res.data);
      setShowSettings(false);
    } catch {}
  };

  // Filter issue summary by severity tab
  const filteredSummary =
    activeTab === "all"
      ? issueSummary
      : issueSummary.filter((s) => s.severity === activeTab);

  // Get issues for a specific type
  const issuesForType = (type) => issues.filter((i) => i.issue_type === type);

  if (!selectedSite) {
    return (
      <div className="text-gray-500 text-center py-20">
        Select a website to view audits
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Site Audit</h2>
        <div className="flex gap-2">
          {latestAudit && (
            <button
              onClick={generateAuditPdf}
              disabled={generatingPdf}
              className="flex items-center gap-2 px-4 py-2 bg-dark-300 hover:bg-dark-50 text-gray-300 hover:text-white text-sm font-medium rounded-lg transition-colors border border-dark-50 disabled:opacity-50"
            >
              <FileText size={14} />
              {generatingPdf ? "Generating..." : "Export PDF"}
            </button>
          )}
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="flex items-center gap-2 px-3 py-2 bg-dark-300 hover:bg-dark-50 text-gray-400 hover:text-white text-sm rounded-lg transition-colors border border-dark-50"
          >
            <Settings size={14} />
          </button>
          <div className="relative group">
            <button
              onClick={() => runAudit("full")}
              disabled={running}
              className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {running ? (
                <Loader size={14} className="animate-spin" />
              ) : (
                <Play size={14} />
              )}
              {running ? "Running..." : "Run Audit"}
            </button>
            {!running && (
              <div className="hidden group-hover:block absolute right-0 top-full mt-1 bg-dark-300 border border-dark-50 rounded-lg shadow-xl z-10 min-w-[160px]">
                <button
                  onClick={() => runAudit("full")}
                  className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:text-white hover:bg-dark-50 rounded-t-lg"
                >
                  Full Audit
                </button>
                <button
                  onClick={() => runAudit("quick")}
                  className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:text-white hover:bg-dark-50 rounded-b-lg"
                >
                  Quick Scan
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Running banner with progress */}
      {running && (
        <div className="bg-copper/10 border border-copper/30 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <Loader size={18} className="animate-spin text-copper" />
            <span className="text-copper font-medium text-sm">
              Audit in progress
            </span>
            <span className="text-gray-500 text-sm">
              {latestAudit?.current_phase || "Initializing..."}
            </span>
          </div>
          {latestAudit?.pages_total > 0 && (
            <div className="ml-8">
              <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                <span>
                  Page {latestAudit.pages_crawled || 0} of{" "}
                  {latestAudit.pages_total}
                </span>
                <span>
                  {Math.round(
                    ((latestAudit.pages_crawled || 0) /
                      latestAudit.pages_total) *
                      100
                  )}
                  %
                </span>
              </div>
              <div className="w-full bg-dark-400 rounded-full h-1.5">
                <div
                  className="bg-copper h-1.5 rounded-full transition-all"
                  style={{
                    width: `${Math.min(
                      100,
                      ((latestAudit.pages_crawled || 0) /
                        latestAudit.pages_total) *
                        100
                    )}%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Settings panel */}
      {showSettings && settings && (
        <SettingsPanel
          settings={settings}
          onSave={saveSettings}
          onClose={() => setShowSettings(false)}
        />
      )}

      {loading ? (
        <div className="animate-pulse space-y-4">
          <div className="h-24 bg-dark-300 rounded-xl" />
          <div className="h-96 bg-dark-300 rounded-xl" />
        </div>
      ) : !latestAudit || latestAudit.status === "running" ? (
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-12 text-center text-gray-500">
          {latestAudit?.status === "running"
            ? "Audit in progress..."
            : 'No audits run yet. Click "Run Audit" to start.'}
        </div>
      ) : (
        <>
          {/* Top bar: last audit info */}
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Clock size={12} />
              Last {latestAudit.audit_type === "quick" ? "quick scan" : "full audit"}:{" "}
              {new Date(latestAudit.completed_at).toLocaleDateString()}
            </span>
            {auditHistory.length > 1 && (
              <span>
                Previous:{" "}
                {new Date(
                  auditHistory[1]?.completed_at
                ).toLocaleDateString()}
              </span>
            )}
          </div>

          {/* Score + Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-5 flex flex-col items-center justify-center">
              <ScoreRing
                score={latestAudit.overall_score}
                label="Health"
                size={90}
              />
              {latestAudit.score_change != null && (
                <div
                  className={`flex items-center gap-1 mt-2 text-xs font-medium ${
                    latestAudit.score_change > 0
                      ? "text-green-400"
                      : latestAudit.score_change < 0
                      ? "text-red-400"
                      : "text-gray-500"
                  }`}
                >
                  {latestAudit.score_change > 0 ? (
                    <ArrowUp size={12} />
                  ) : latestAudit.score_change < 0 ? (
                    <ArrowDown size={12} />
                  ) : null}
                  {latestAudit.score_change > 0 ? "+" : ""}
                  {latestAudit.score_change} from last
                </div>
              )}
            </div>
            {[
              {
                label: "Pages Crawled",
                value: latestAudit.pages_crawled,
                icon: FileText,
              },
              {
                label: "Errors",
                value: latestAudit.critical_count,
                color: "text-red-400",
                icon: AlertTriangle,
              },
              {
                label: "Warnings",
                value: latestAudit.warning_count,
                color: "text-amber-400",
                icon: AlertCircle,
              },
              {
                label: "Notices",
                value: latestAudit.info_count,
                color: "text-blue-400",
                icon: Info,
              },
            ].map(({ label, value, color, icon: Icon }) => (
              <div
                key={label}
                className="bg-dark-300 border border-dark-50 rounded-xl p-5"
              >
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                  <Icon size={12} />
                  {label}
                </div>
                <div
                  className={`text-2xl font-mono font-bold ${
                    color || "text-white"
                  }`}
                >
                  {value}
                </div>
              </div>
            ))}
          </div>

          {/* View tabs */}
          <div className="flex gap-1 bg-dark-300 border border-dark-50 rounded-lg p-1">
            {[
              { id: "issues", label: "Issues", icon: AlertTriangle },
              { id: "history", label: "History", icon: BarChart3 },
              { id: "images", label: "Images", icon: Image },
              { id: "links", label: "Links", icon: Link2 },
              { id: "crawler", label: "Crawler", icon: Search },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveView(id)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeView === id
                    ? "bg-copper/20 text-copper"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                <Icon size={14} />
                {label}
              </button>
            ))}
          </div>

          {/* ISSUES VIEW */}
          {activeView === "issues" && (
            <>
              {/* Severity filter tabs */}
              <div className="flex gap-2">
                {["all", "critical", "warning", "info"].map((sev) => {
                  const conf = severityConfig[sev];
                  const count =
                    sev === "all"
                      ? issueSummary.length
                      : issueSummary.filter((s) => s.severity === sev).length;
                  return (
                    <button
                      key={sev}
                      onClick={() => setActiveTab(sev)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        activeTab === sev
                          ? "bg-copper/20 text-copper"
                          : "bg-dark-300 text-gray-400 hover:text-white"
                      }`}
                    >
                      {sev === "all"
                        ? "All Issues"
                        : conf?.label || sev.charAt(0).toUpperCase() + sev.slice(1)}
                      <span className="ml-1.5 text-gray-500">{count}</span>
                    </button>
                  );
                })}
              </div>

              {/* Issue type list (grouped like Ahrefs) */}
              <div className="space-y-2">
                {filteredSummary.map((summary) => {
                  const config = severityConfig[summary.severity];
                  const Icon = config.icon;
                  const isExpanded = expandedType === summary.issue_type;
                  const typeIssues = issuesForType(summary.issue_type);

                  return (
                    <div
                      key={summary.issue_type}
                      className={`${config.bg} border ${config.border} rounded-lg overflow-hidden`}
                    >
                      {/* Issue type header row */}
                      <button
                        onClick={() =>
                          setExpandedType(isExpanded ? null : summary.issue_type)
                        }
                        className="w-full flex items-center gap-3 p-4 hover:bg-white/5 transition-colors"
                      >
                        <div
                          className={`w-2 h-2 rounded-full ${config.dotColor}`}
                        />
                        <Icon
                          size={16}
                          className={`${config.color} flex-shrink-0`}
                        />
                        <span className="text-sm font-medium text-gray-200 flex-1 text-left">
                          {ISSUE_LABELS[summary.issue_type] || summary.label}
                        </span>
                        <span className="text-sm font-mono font-bold text-gray-300">
                          {summary.count}
                        </span>
                        {summary.change !== 0 && (
                          <span
                            className={`text-xs font-medium flex items-center gap-0.5 ${
                              summary.change > 0
                                ? "text-red-400"
                                : "text-green-400"
                            }`}
                          >
                            {summary.change > 0 ? (
                              <ArrowUp size={10} />
                            ) : (
                              <ArrowDown size={10} />
                            )}
                            {summary.change > 0 ? "+" : ""}
                            {summary.change}
                          </span>
                        )}
                        {summary.is_new && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-red-900/40 text-red-400">
                            NEW
                          </span>
                        )}
                        {isExpanded ? (
                          <ChevronDown
                            size={16}
                            className="text-gray-500"
                          />
                        ) : (
                          <ChevronRight
                            size={16}
                            className="text-gray-500"
                          />
                        )}
                      </button>

                      {/* Expanded: show affected URLs */}
                      {isExpanded && (
                        <div className="border-t border-white/5 bg-black/20">
                          <div className="max-h-96 overflow-y-auto">
                            {typeIssues.map((issue, i) => (
                              <div
                                key={i}
                                className="flex items-start gap-3 px-4 py-3 border-b border-white/5 last:border-b-0"
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs text-gray-300 truncate">
                                    {issue.page_url}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {issue.description}
                                  </div>
                                  {issue.recommendation && (
                                    <div className="text-xs text-gray-600 mt-0.5 italic">
                                      {issue.recommendation}
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span
                                    className={`text-xs px-1.5 py-0.5 rounded ${
                                      issue.status === "new"
                                        ? "bg-blue-900/30 text-blue-400"
                                        : issue.status === "recurring"
                                        ? "bg-amber-900/30 text-amber-400"
                                        : issue.status === "fixed"
                                        ? "bg-green-900/30 text-green-400"
                                        : "bg-gray-800 text-gray-400"
                                    }`}
                                  >
                                    {issue.status}
                                  </span>
                                  <a
                                    href={issue.page_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-gray-500 hover:text-white"
                                  >
                                    <ExternalLink size={12} />
                                  </a>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {!filteredSummary.length && (
                  <div className="text-gray-500 text-center py-8">
                    {activeTab === "all"
                      ? "No issues found — site is healthy!"
                      : `No ${activeTab} issues found`}
                  </div>
                )}
              </div>
            </>
          )}

          {/* HISTORY VIEW */}
          {activeView === "history" && (
            <div className="space-y-4">
              {/* Trend mini-chart */}
              {historyTrend.length > 1 && (
                <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
                  <h3 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
                    <TrendingUp size={14} /> Health Score Trend
                  </h3>
                  <div className="flex items-end gap-1 h-32">
                    {historyTrend.map((point, i) => (
                      <div
                        key={i}
                        className="flex-1 flex flex-col items-center gap-1"
                      >
                        <span className="text-xs text-gray-500">
                          {point.score}
                        </span>
                        <div
                          className={`w-full rounded-t transition-all ${
                            point.score >= 90
                              ? "bg-green-500/60"
                              : point.score >= 70
                              ? "bg-amber-500/60"
                              : "bg-red-500/60"
                          }`}
                          style={{
                            height: `${Math.max(4, point.score)}%`,
                          }}
                        />
                        <span className="text-[10px] text-gray-600 truncate w-full text-center">
                          {point.date
                            ? new Date(point.date).toLocaleDateString(
                                "en",
                                { month: "short", day: "numeric" }
                              )
                            : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Audit history list */}
              <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
                <div className="grid grid-cols-7 gap-4 px-4 py-3 text-xs text-gray-500 font-medium border-b border-dark-50">
                  <span>Date</span>
                  <span>Type</span>
                  <span>Score</span>
                  <span>Pages</span>
                  <span className="text-red-400">Errors</span>
                  <span className="text-amber-400">Warnings</span>
                  <span className="text-blue-400">Notices</span>
                </div>
                {auditHistory.map((audit) => (
                  <div
                    key={audit.id}
                    className="grid grid-cols-7 gap-4 px-4 py-3 text-sm border-b border-dark-50/50 last:border-b-0 hover:bg-dark-50/30"
                  >
                    <span className="text-gray-400">
                      {audit.completed_at
                        ? new Date(audit.completed_at).toLocaleDateString()
                        : "—"}
                    </span>
                    <span className="text-gray-400 capitalize">
                      {audit.audit_type}
                    </span>
                    <span
                      className={`font-mono font-bold ${
                        audit.overall_score >= 90
                          ? "text-green-400"
                          : audit.overall_score >= 70
                          ? "text-amber-400"
                          : "text-red-400"
                      }`}
                    >
                      {audit.overall_score}
                      {audit.score_change != null && (
                        <span
                          className={`ml-1 text-xs ${
                            audit.score_change > 0
                              ? "text-green-400"
                              : audit.score_change < 0
                              ? "text-red-400"
                              : "text-gray-600"
                          }`}
                        >
                          {audit.score_change > 0 ? "+" : ""}
                          {audit.score_change}
                        </span>
                      )}
                    </span>
                    <span className="text-gray-400">{audit.pages_crawled}</span>
                    <span className="text-red-400 font-mono">
                      {audit.critical_count}
                    </span>
                    <span className="text-amber-400 font-mono">
                      {audit.warning_count}
                    </span>
                    <span className="text-blue-400 font-mono">
                      {audit.info_count}
                    </span>
                  </div>
                ))}
                {!auditHistory.length && (
                  <div className="text-gray-500 text-center py-8">
                    No audit history yet
                  </div>
                )}
              </div>
            </div>
          )}

          {/* IMAGES VIEW */}
          {activeView === "images" && (
            <ImageReportView report={latestAudit?.image_report} />
          )}

          {/* LINKS VIEW */}
          {activeView === "links" && (
            <LinkReportView report={latestAudit?.link_report} />
          )}

          {/* CRAWLER VIEW */}
          {activeView === "crawler" && (
            <CrawlDashboard selectedSite={selectedSite} />
          )}
        </>
      )}
    </div>
  );
}

function ImageReportView({ report }) {
  if (!report) {
    return (
      <div className="bg-dark-300 border border-dark-50 rounded-xl p-8 text-center text-gray-500">
        Image audit data not available. Run a full audit to generate the image report.
      </div>
    );
  }

  const sections = [
    {
      title: "Oversized Images",
      count: report.oversized_count,
      items: report.oversized,
      color: "text-red-400",
    },
    {
      title: "Missing Alt Text",
      count: report.missing_alt_count,
      items: report.missing_alt,
      color: "text-amber-400",
    },
    {
      title: "Broken Images",
      count: report.broken_count,
      items: report.broken,
      color: "text-red-400",
    },
    {
      title: "Missing Dimensions",
      count: report.missing_dimensions_count,
      items: report.missing_dimensions,
      color: "text-blue-400",
    },
    {
      title: "Could Convert to WebP",
      count: report.could_be_webp_count,
      items: report.could_be_webp,
      color: "text-blue-400",
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Images" value={report.total_images} />
        <StatCard
          label="Oversized"
          value={report.oversized_count}
          color="text-red-400"
        />
        <StatCard
          label="Missing Alt"
          value={report.missing_alt_count}
          color="text-amber-400"
        />
        <StatCard
          label="Broken"
          value={report.broken_count}
          color="text-red-400"
        />
      </div>

      {sections.map(
        (section) =>
          section.count > 0 && (
            <CollapsibleSection
              key={section.title}
              title={section.title}
              count={section.count}
              color={section.color}
            >
              {(section.items || []).slice(0, 50).map((img, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 px-4 py-2 border-b border-white/5 last:border-b-0 text-xs"
                >
                  <span className="text-gray-400 truncate flex-1">
                    {img.src?.split("/").pop() || img.src}
                  </span>
                  {img.size_kb > 0 && (
                    <span className="text-gray-500">{img.size_kb}KB</span>
                  )}
                  <span className="text-gray-600 truncate max-w-[200px]">
                    {img.page_url?.replace(/https?:\/\/[^/]+/, "")}
                  </span>
                </div>
              ))}
            </CollapsibleSection>
          )
      )}
    </div>
  );
}

function LinkReportView({ report }) {
  if (!report) {
    return (
      <div className="bg-dark-300 border border-dark-50 rounded-xl p-8 text-center text-gray-500">
        Link health data not available. Run a full audit to generate the link report.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Pages" value={report.total_pages} />
        <StatCard label="Internal Links" value={report.total_internal_links} />
        <StatCard
          label="Broken Links"
          value={report.broken_link_count}
          color="text-red-400"
        />
        <StatCard
          label="Redirect Links"
          value={report.redirect_link_count}
          color="text-amber-400"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <StatCard
          label="Orphan Pages"
          value={report.orphan_count}
          color="text-amber-400"
        />
        <StatCard
          label="Avg Inbound Links"
          value={report.avg_inbound_links}
        />
      </div>

      {report.orphan_count > 0 && (
        <CollapsibleSection
          title="Orphan Pages"
          count={report.orphan_count}
          color="text-amber-400"
        >
          {(report.orphan_pages || []).slice(0, 50).map((url, i) => (
            <div
              key={i}
              className="px-4 py-2 border-b border-white/5 last:border-b-0 text-xs text-gray-400 truncate"
            >
              {url}
            </div>
          ))}
        </CollapsibleSection>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className={`text-xl font-mono font-bold ${color || "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

function CollapsibleSection({ title, count, color, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-dark-50/30 transition-colors"
      >
        <span className={`text-sm font-medium ${color}`}>{title}</span>
        <span className="text-sm font-mono text-gray-400">{count}</span>
        <div className="flex-1" />
        {open ? (
          <ChevronDown size={14} className="text-gray-500" />
        ) : (
          <ChevronRight size={14} className="text-gray-500" />
        )}
      </button>
      {open && (
        <div className="border-t border-dark-50 max-h-80 overflow-y-auto">
          {children}
        </div>
      )}
    </div>
  );
}

function SettingsPanel({ settings, onSave, onClose }) {
  const [form, setForm] = useState(settings);

  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-200">Audit Settings</h3>
        <button onClick={onClose} className="text-gray-500 hover:text-white">
          <X size={16} />
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <label className="space-y-1">
          <span className="text-xs text-gray-500">Full Audit Frequency</span>
          <select
            value={form.crawl_frequency}
            onChange={(e) =>
              setForm({ ...form, crawl_frequency: e.target.value })
            }
            className="w-full bg-dark-400 border border-dark-50 rounded-lg px-3 py-2 text-sm text-gray-300"
          >
            <option value="monthly">Monthly</option>
            <option value="biweekly">Bi-weekly</option>
            <option value="weekly">Weekly</option>
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs text-gray-500">Quick Scan Frequency</span>
          <select
            value={form.quick_scan_frequency}
            onChange={(e) =>
              setForm({ ...form, quick_scan_frequency: e.target.value })
            }
            className="w-full bg-dark-400 border border-dark-50 rounded-lg px-3 py-2 text-sm text-gray-300"
          >
            <option value="weekly">Weekly</option>
            <option value="daily">Daily</option>
            <option value="off">Off</option>
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs text-gray-500">Max Pages</span>
          <select
            value={form.max_pages}
            onChange={(e) =>
              setForm({ ...form, max_pages: parseInt(e.target.value) })
            }
            className="w-full bg-dark-400 border border-dark-50 rounded-lg px-3 py-2 text-sm text-gray-300"
          >
            <option value={100}>100</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
            <option value={5000}>Unlimited</option>
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs text-gray-500">Crawl Speed</span>
          <select
            value={form.crawl_speed}
            onChange={(e) =>
              setForm({ ...form, crawl_speed: e.target.value })
            }
            className="w-full bg-dark-400 border border-dark-50 rounded-lg px-3 py-2 text-sm text-gray-300"
          >
            <option value="slow">Slow (1/sec)</option>
            <option value="normal">Normal (3/sec)</option>
            <option value="fast">Fast (5/sec)</option>
          </select>
        </label>
        <label className="space-y-1 flex items-end gap-2">
          <input
            type="checkbox"
            checked={form.email_report}
            onChange={(e) =>
              setForm({ ...form, email_report: e.target.checked })
            }
            className="rounded"
          />
          <span className="text-xs text-gray-500">Email Reports</span>
        </label>
        <label className="space-y-1 flex items-end gap-2">
          <input
            type="checkbox"
            checked={form.auto_fix_enabled}
            onChange={(e) =>
              setForm({ ...form, auto_fix_enabled: e.target.checked })
            }
            className="rounded"
          />
          <span className="text-xs text-gray-500">Auto-Fix</span>
        </label>
      </div>
      <label className="block space-y-1">
        <span className="text-xs text-gray-500">
          Ignore Patterns (one per line)
        </span>
        <textarea
          value={form.ignore_patterns}
          onChange={(e) =>
            setForm({ ...form, ignore_patterns: e.target.value })
          }
          rows={3}
          placeholder="/tag/*&#10;/author/*&#10;/page/*"
          className="w-full bg-dark-400 border border-dark-50 rounded-lg px-3 py-2 text-sm text-gray-300 font-mono"
        />
      </label>
      <div className="flex justify-end">
        <button
          onClick={() => onSave(form)}
          className="px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg transition-colors"
        >
          Save Settings
        </button>
      </div>
    </div>
  );
}
